import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUser, FaEnvelope, FaPhone, FaBirthdayCake, FaCalendarAlt, FaSignOutAlt, FaEdit, FaSave, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { clientSignOut } from '../../../../../utils/firebase';
import { updateClientDobByClientId, getClientById } from '../../../../../crud/clients_api/clients';
import './Profile.scss';

const Profile: React.FC = () => {
    const navigate = useNavigate();
    const [client, setClient] = useState<any>(null);
    const [isEditingDob, setIsEditingDob] = useState(false);
    const [newDob, setNewDob] = useState('');
    const [dobUpdateInfo, setDobUpdateInfo] = useState<{
        updatesRemaining: number;
        updateHistory: any[];
    } | null>(null);

    useEffect(() => {
        const clientData = localStorage.getItem('client');
        if (clientData) {
            const parsedClient = JSON.parse(clientData);
            setClient(parsedClient);
            
            // Fetch updated client info to get DOB metadata
            if (parsedClient.client_id) {
                fetchClientInfo(parsedClient.client_id);
            }
        }
    }, []);

    const fetchClientInfo = async (clientId: number) => {
        try {
            const response = await getClientById(clientId);
            if (response.data) {
                const clientData = response.data;
                
                // Calculate updates remaining from metadata
                const dobUpdates = clientData.client_metadata?.dob_updates || [];
                setDobUpdateInfo({
                    updatesRemaining: 2 - dobUpdates.length,
                    updateHistory: dobUpdates
                });
                
                // Update local storage with latest client data
                localStorage.setItem('client', JSON.stringify(clientData));
                setClient(clientData);
            }
        } catch (error) {
            console.error('Error fetching client info:', error);
        }
    };

    const handleEditDob = () => {
        setIsEditingDob(true);
        setNewDob(client.dob || '');
    };

    const handleCancelEditDob = () => {
        setIsEditingDob(false);
        setNewDob('');
    };

    const handleSaveDob = async () => {
        if (!newDob) {
            toast.error('Por favor ingresa una fecha válida');
            return;
        }

        // Validate date format
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (!dateRegex.test(newDob)) {
            toast.error('Formato de fecha inválido. Usa AAAA-MM-DD');
            return;
        }

        try {
            const response = await updateClientDobByClientId(client.client_id, newDob);
            
            if ((response as any).status === 200) {
                toast.success((response as any).data.message || 'Fecha de nacimiento actualizada exitosamente');
                
                // Update local state
                setClient({ ...client, dob: newDob });
                setIsEditingDob(false);
                
                // Update DOB info
                setDobUpdateInfo({
                    updatesRemaining: (response as any).data.updates_remaining || 0,
                    updateHistory: (response as any).data.update_history || []
                });
                
                // Refresh client data
                await fetchClientInfo(client.client_id);
            }
        } catch (error: any) {
            console.error('Error updating date of birth:', error);
            
            if (error.response?.status === 403) {
                toast.error('Has alcanzado el límite máximo de actualizaciones de fecha de nacimiento (2 veces)');
            } else {
                const message = error.response?.data?.message || 'Error al actualizar la fecha de nacimiento';
                toast.error(message);
            }
        }
    };

    const handleLogout = async () => {
        // Clear all client-related data from localStorage
        localStorage.removeItem('access_token');
        localStorage.removeItem('client');
        
        // Clear any pending operations from sessionStorage
        sessionStorage.removeItem('pending_local_id');
        
        // Sign out from Firebase if using Google auth
        try {
            await clientSignOut();
        } catch (error) {
            console.error('Error signing out from Firebase:', error);
        }
        
        // Navigate to auth page after clearing everything
        navigate('/ClientAuth/ClientPortal/Home');
    };

    if (!client) {
        return (
            <div className="client-profile-container">
                <p>Cargando...</p>
            </div>
        );
    }

    return (
        <div className="client-profile-container">
            <div className="profile-content">
                <div className="profile-header">
                    <div className="profile-avatar">
                        <FaUser />
                    </div>
                    <h2 className="profile-name">{client.username || 'Cliente'}</h2>
                </div>

                <div className="profile-info-section">
                    <h3 className="section-title">Información Personal</h3>
                    
                    <div className="info-item">
                        <div className="info-icon">
                            <FaUser />
                        </div>
                        <div className="info-content">
                            <span className="info-label">Nombre</span>
                            <span className="info-value">{client.username || 'No disponible'}</span>
                        </div>
                    </div>

                    <div className="info-item">
                        <div className="info-icon">
                            <FaEnvelope />
                        </div>
                        <div className="info-content">
                            <span className="info-label">Correo Electrónico</span>
                            <span className="info-value">{client.email || 'No disponible'}</span>
                        </div>
                    </div>

                    {client.phone && (
                        <div className="info-item">
                            <div className="info-icon">
                                <FaPhone />
                            </div>
                            <div className="info-content">
                                <span className="info-label">Teléfono</span>
                                <span className="info-value">{client.phone}</span>
                            </div>
                        </div>
                    )}

                    <div className="info-item dob-item">
                        <div className="info-icon">
                            <FaBirthdayCake />
                        </div>
                        <div className="info-content">
                            <span className="info-label">Fecha de Nacimiento</span>
                            {isEditingDob ? (
                                <div className="dob-edit-container">
                                    <input
                                        type="date"
                                        value={newDob}
                                        onChange={(e) => setNewDob(e.target.value)}
                                        className="dob-input"
                                        max={new Date().toISOString().split('T')[0]}
                                    />
                                    <div className="dob-edit-actions">
                                        <button 
                                            className="dob-save-btn" 
                                            onClick={handleSaveDob}
                                            title="Guardar"
                                        >
                                            <FaSave />
                                        </button>
                                        <button 
                                            className="dob-cancel-btn" 
                                            onClick={handleCancelEditDob}
                                            title="Cancelar"
                                        >
                                            <FaTimes />
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="dob-display-container">
                                    <span className="info-value">
                                        {client.dob 
                                            ? new Date(client.dob).toLocaleDateString('es-ES')
                                            : 'No disponible'}
                                    </span>
                                    {(!client.dob || (dobUpdateInfo && dobUpdateInfo.updatesRemaining > 0)) && (
                                        <button 
                                            className="dob-edit-btn" 
                                            onClick={handleEditDob}
                                            title={client.dob ? "Editar fecha de nacimiento" : "Agregar fecha de nacimiento"}
                                        >
                                            <FaEdit />
                                        </button>
                                    )}
                                </div>
                            )}
                            {dobUpdateInfo !== null && (
                                <div className="dob-info-message">
                                    {dobUpdateInfo.updatesRemaining > 0 ? (
                                        <span className="dob-updates-remaining">
                                            Puedes actualizar tu fecha de nacimiento {dobUpdateInfo.updatesRemaining} {dobUpdateInfo.updatesRemaining === 1 ? 'vez' : 'veces'} más
                                        </span>
                                    ) : (
                                        <span className="dob-updates-locked">
                                            Has alcanzado el límite de actualizaciones (2/2). Tu fecha de nacimiento está bloqueada.
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {client.created_at && (
                        <div className="info-item">
                            <div className="info-icon">
                                <FaCalendarAlt />
                            </div>
                            <div className="info-content">
                                <span className="info-label">Miembro desde</span>
                                <span className="info-value">
                                    {new Date(client.created_at).toLocaleDateString('es-ES')}
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                <button className="logout-button" onClick={handleLogout}>
                    <FaSignOutAlt />
                    <span>Cerrar Sesión</span>
                </button>
            </div>
        </div>
    );
};

export default Profile;
